import { memberMessage, renderMemberMessage } from '../lib/memberMessages'
import { useOptionalPrivanaI18n as usePortalPresentationI18n } from '@/i18n/PrivanaI18nProvider.jsx'
import { nextProfileReadRevision } from '../lib/memberProfileRequest'
import { enqueueSportWrite, waitForSportWrites } from '../lib/sportLevelPersistence'
import { profileMessageDescriptor, profileErrorDescriptor } from '../lib/profileMessages'
import MemberAchievements from './MemberAchievements'
import MemberDigitalMembershipCard from './MemberDigitalMembershipCard'
import MemberSecurity from './MemberSecurity'
import MemberFrontDeskModal from '../components/MemberFrontDeskModal'
import { useSearchParams } from 'react-router-dom'
import { useMembersConversationOverlay, useMembersNativeBack } from '../lib/useMembersConversationOverlay'
import MemberMembershipInformation from './MemberMembershipInformation'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  Award,
  CalendarClock,
  Camera,
  Car,
  Check,
  CreditCard,
  IdCard,
  Mail,
  Phone,
  LockKeyhole,
  LogOut,
  ShieldCheck,
  Sparkles,
  Trophy,
  ChevronDown,
  RotateCcw,
  Trash2,
  Pencil,
  Plus,
  UserPlus,
  UserRound,
  Languages,
  X,
} from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { useToast } from '@/components/ui/use-toast'
import { sportLevels } from '../constants/sportLevels'
import { useProfileOverview } from '../lib/useProfileOverview'
import { formatProfileCalendarDate, localizeMembershipStatus } from '../lib/profileOverview'
import { normalizeDateOnly } from '../../../shared/dateOnly.mjs'
import TimedMemberImage from '../components/TimedMemberImage'
import { emitMemberClientTelemetry } from '../lib/diagnosticTelemetry'
import { usePrivanaI18n } from '@/i18n/PrivanaI18nProvider.jsx'
import { translate } from '@/i18n/index.js'
import LanguageSelector from '@/i18n/LanguageSelector'
import { PRIVANA_LOCALES } from '../../../shared/locales.mjs'

const ConfirmContext = React.createContext(null)

export function CanonicalSportIcon({ sport }) {
  if (!sport.mobileMaskSrc) return <Trophy className="mp-sport-icon__fallback" size={21} aria-hidden="true" />

  const maskStyle = { '--mp-sport-icon-url': `url("${sport.mobileMaskSrc}")` }
  return <span className="mp-sport-icon__mask" style={maskStyle} aria-hidden="true" />
}

export function formatRelationshipLabel(value) {
  const labels = { spouse: 'Spouse', partner: 'Partner', parent: 'Parent', child: 'Child', sibling: 'Sibling', grandparent: 'Grandparent', grandchild: 'Grandchild', guardian: 'Guardian', dependant: 'Dependant', other: 'Other' }
  return labels[String(value || '').toLowerCase()] || 'Other'
}

function MemberPortalConfirmDialog({ request, onResolve }) {
  const { t: tx } = usePortalPresentationI18n()
  const cancelRef = useRef(null)
  const confirmRef = useRef(null)
  const previousFocusRef = useRef(null)
  useEffect(() => {
    if (!request) return undefined
    previousFocusRef.current = document.activeElement
    const t = setTimeout(() => cancelRef.current?.focus(), 0)
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onResolve(false) }
      if (event.key === 'Tab') {
        const nodes = [cancelRef.current, confirmRef.current].filter(Boolean)
        if (!nodes.length) return
        const first = nodes[0], last = nodes[nodes.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKeyDown); previousFocusRef.current?.focus?.() }
  }, [request, onResolve])
  if (!request) return null
  const tone = request.tone === 'danger' ? ' mp-confirm-modal__button--danger' : ''
  return <div className="mp-confirm-modal" role="presentation"><button type="button" className="mp-confirm-modal__backdrop" aria-label={renderMemberMessage(tx, request.cancelLabel) || tx("common.actions.close")} onClick={() => onResolve(false)} /><div className="mp-confirm-modal__panel" role="dialog" aria-modal="true" aria-labelledby="mp-confirm-title" aria-describedby="mp-confirm-description"><div className="mp-confirm-modal__icon" aria-hidden="true"><ShieldCheck size={22}/></div><h4 id="mp-confirm-title">{renderMemberMessage(tx, request.title)}</h4><p id="mp-confirm-description">{renderMemberMessage(tx, request.description)}</p>{request.detail ? <p>{renderMemberMessage(tx, request.detail)}</p> : null}<div className="mp-confirm-modal__actions"><button ref={cancelRef} type="button" className="mp-ghost mp-confirm-modal__button" onClick={() => onResolve(false)}>{renderMemberMessage(tx, request.cancelLabel) || tx("racquetPlay.keepEditing")}</button><button ref={confirmRef} type="button" className={`mp-primary mp-confirm-modal__button${tone}`} onClick={() => onResolve(true)}>{renderMemberMessage(tx, request.confirmLabel) || tx("memberWorkflows.profile.discard")}</button></div></div></div>
}

function MemberPortalConfirmProvider({ children }) {
  const [request, setRequest] = useState(null)
  const resolverRef = useRef(null)
  const confirm = useCallback((options) => new Promise((resolve) => { resolverRef.current = resolve; setRequest(options || {}) }), [])
  const resolve = useCallback((approved) => { const resolver = resolverRef.current; resolverRef.current = null; setRequest(null); resolver?.(approved) }, [])
  return <ConfirmContext.Provider value={confirm}>{children}<MemberPortalConfirmDialog request={request} onResolve={resolve} /></ConfirmContext.Provider>
}

function useConfirm() {
  const confirm = React.useContext(ConfirmContext)
  if (!confirm) throw new Error('useConfirm must be used inside MemberPortalConfirmProvider')
  return confirm
}

const LEVEL_LABELS = ['Recreational', 'Social', 'Club', 'Competitive', 'Elite']
const MAX_FILE_SIZE = 8 * 1024 * 1024
const PROFILE_SECTIONS = [
  {
    id: 'personal-details',
    title: 'Personal Details',
    description: 'Manage your personal information.',
    placeholder: 'This section will allow members to manage their personal information.',
    bullets: [
      'Update your contact information',
      'Manage emergency contacts',
      'Keep your member profile current',
    ],
    icon: UserRound,
    accent: 'indigo',
  },
  {
    id: 'sports-playing-levels',
    title: 'Sports & Playing Levels',
    description: 'Fine-tune your ratings and handicaps.',
    icon: Trophy,
    accent: 'amber',
  },
  {
    id: 'achievements',
    title: 'Achievements',
    description: 'Celebrate milestones and club moments.',
    placeholder: 'This section will showcase member achievements, awards, and club milestones.',
    bullets: [
      'View club milestones',
      'Track competition achievements',
      'Celebrate personal accomplishments',
    ],
    icon: Award,
    accent: 'green',
  },
  {
    id: 'membership-information',
    title: 'Membership Information',
    description: 'Review your membership status and plan.',
    placeholder: 'This section will allow members to review their membership information.',
    bullets: [
      'Review your membership',
      'View current status',
      'Check renewal information',
    ],
    icon: IdCard,
    accent: 'blue',
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Control account access and protection.',
    placeholder: 'This section will allow members to manage account security settings.',
    bullets: [
      'Manage account security',
      'Review password settings',
      'Strengthen sign-in protection',
    ],
    icon: LockKeyhole,
    accent: 'red',
  },
  {
    id: 'digital-membership-card',
    title: 'Digital Membership Card',
    description: 'Access your premium member pass.',
    icon: CreditCard,
    accent: 'cyan',
  },
]

const DEFAULT_LEVELS = { golf: 0, tennis: 3, padel: 3, squash: 3, pickleball: 3 }
export function formatGolfHandicap(value) {
  if (value == null || value === '') return 'Not set'
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 'Not set'
  const magnitude = Math.abs(parsed).toFixed(1)
  return parsed > 0 ? `+${magnitude}` : magnitude
}

export function parseGolfHandicapEntry(value) {
  const text = String(value ?? '').trim()
  if (!/^\+?\d+(?:\.\d)?$/.test(text)) return null
  const displayed = Number(text)
  const isPlus = text.startsWith('+')
  if (!Number.isFinite(displayed) || displayed > (isPlus ? 5 : 36)) return null
  return isPlus ? displayed : -displayed
}

export function formatSportLevel(value) {
  if (value == null || !Number.isFinite(Number(value))) return { primary: 'Not set', secondary: 'Add your level for better matches and recommendations.' }
  const normalized = normalizeRatingValue(value)
  return { primary: levelLabel(normalized), secondary: `Level ${normalized.toFixed(1)}` }
}

function levelLabel(value) {
  const idx = Math.min(Math.max(Math.round(value) - 1, 0), LEVEL_LABELS.length - 1)
  return LEVEL_LABELS[idx]
}

function normalizeRatingValue(value, fallback = 3) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.round(Math.min(Math.max(parsed, 1), 5) * 10) / 10
}

function normalizeGolfValue(value, fallback = 0) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  if (parsed < -36) return -36
  if (parsed > 5) return 5
  return parsed
}

function normalizePortraitMode(value) {
  return value === 'photo' ? 'photo' : 'live'
}

export function deriveMembershipPresentation(profile) {
  const rawStatus = profile?.status || profile?.account_status || profile?.membership_status
  const statusLabel = typeof rawStatus === 'string' && rawStatus.trim()
    ? rawStatus.trim().replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : null
  const normalizedStatus = String(rawStatus || '').trim().toLowerCase().replace(/[\s_-]+/g, '-')
  const statusVariant = ['active', 'current', 'approved'].includes(normalizedStatus)
    ? 'success'
    : ['pending', 'pending-approval', 'on-hold'].includes(normalizedStatus)
      ? 'subtle'
      : ['suspended', 'inactive', 'expired', 'cancelled', 'canceled', 'terminated'].includes(normalizedStatus)
        ? 'danger'
        : 'muted'
  const accessLabel = profile?.access_label
    || profile?.accessLabel
    || profile?.membership_type
    || profile?.membershipType
    || profile?.plan_name
    || null

  return { statusLabel, statusVariant, accessLabel }
}

function ProfileSectionNavigation({ onSelectSection }) {
  const { t } = usePrivanaI18n()
  return (
    <nav className="mp-profile-section-nav" aria-label={t('profileOverview.sections')}>
      {PROFILE_SECTIONS.map((section) => {
        const Icon = section.icon
        return (
          <button
            key={section.id}
            type="button"
            className={`mp-profile-nav-tile mp-profile-accent--${section.accent}`}
            onClick={() => onSelectSection(section.id)}
          >
            <span className="mp-profile-nav-tile__icon" aria-hidden="true"><Icon size={22} /></span>
            <span className="mp-profile-nav-tile__copy">
              <span className="mp-profile-nav-tile__title">{t(`profileOverview.tiles.${section.id}.title`)}</span>
              <span className="mp-profile-nav-tile__description" title={t(`profileOverview.tiles.${section.id}.description`)}>{t(`profileOverview.tiles.${section.id}.description`)}</span>
            </span>
          </button>
        )
      })}
    </nav>
  )
}


function ProfileHubPanel({ overview, membershipNumber, onViewMembership }) {
  const { t, locale } = usePrivanaI18n()
  const membership = overview.data?.membership
  const unavailable = t('profileOverview.unavailable')
  const state = overview.status === 'loading' ? t('profileOverview.loading') : overview.status === 'failed' ? t('profileOverview.error') : null
  const statusLabel = localizeMembershipStatus(membership?.status, t)
  const items = [
    { label: t('profileOverview.membership'), value: membership?.type || unavailable, detail: statusLabel, Icon: IdCard, accent: 'green' },
    { label: t('profileOverview.since'), value: formatProfileCalendarDate(membership?.start_date, locale) || unavailable, detail: membershipNumber ? t('profileOverview.number', { number: membershipNumber }) : unavailable, Icon: CalendarClock, accent: 'blue' },
    { label: t('profileOverview.renewal'), value: formatProfileCalendarDate(membership?.renewal_date, locale) || t('profileOverview.unavailable'), Icon: RotateCcw, accent: 'indigo' },
  ]
  return <section className="mp-profile-hub-panel" aria-labelledby="profile-hub-title" aria-busy={overview.status === 'loading'}>
    <div className="mp-profile-hub-panel__header"><h3 id="profile-hub-title">{t('profileOverview.title')}</h3></div>
    <div className="mp-profile-hub-divider" aria-hidden="true" />
    <div className="mp-profile-hub-grid">
      {items.map(({ label, value, detail, Icon, accent }, index) => <div key={label} className={`mp-profile-hub-item mp-profile-accent--${accent}`}>
        <span className="mp-profile-soft-icon" aria-hidden="true"><Icon size={21} /></span>
        <div className="mp-profile-hub-copy"><p>{label}</p><strong>{state || value}</strong>
          {!state && detail ? <span>{detail}</span> : null}
          {index === 2 ? <button type="button" className="mp-profile-hub-link" onClick={onViewMembership}>{t('profileOverview.view')} <span aria-hidden="true">→</span></button> : null}
        </div>
      </div>)}
    </div>
  </section>
}


const PROFILE_ENUM_KEYS = {"Mr": "memberWorkflows.profile.mr", "Mrs": "memberWorkflows.profile.mrs", "Miss": "memberWorkflows.profile.miss", "Ms": "memberWorkflows.profile.ms", "Dr": "memberWorkflows.profile.dr", "Prof": "memberWorkflows.profile.prof", "Sir": "memberWorkflows.profile.sir", "Dame": "memberWorkflows.profile.dame", "Lord": "memberWorkflows.profile.lord", "Lady": "memberWorkflows.profile.lady", "Rev": "memberWorkflows.profile.rev", "Other": "membersStaff.other", "Female": "membersStaff.profileChoices.female", "Male": "membersStaff.profileChoices.male", "Non-binary": "membersStaff.profileChoices.nonBinary", "Prefer not to say": "membersStaff.profileChoices.undisclosed", "Single": "membersStaff.profileChoices.single", "Married": "membersStaff.profileChoices.married", "Civil partnership": "membersStaff.profileChoices.civilPartnership", "Partnered": "membersStaff.profileChoices.partnered", "Separated": "membersStaff.profileChoices.separated", "Divorced": "membersStaff.profileChoices.divorced", "Widowed": "membersStaff.profileChoices.widowed", "Home": "roomScreen.home", "Work": "memberWorkflows.profile.work", "Holiday Home": "memberWorkflows.profile.holidayHome", "Postal": "memberWorkflows.profile.postal", "Car": "memberWorkflows.profile.car", "Motorcycle": "memberWorkflows.profile.motorcycle", "Van": "memberWorkflows.profile.van", "Golf buggy": "memberWorkflows.profile.golfBuggy", "Spouse": "membersStaff.spouse", "Partner": "membersStaff.partner", "Parent": "membersStaff.parent", "Child": "membersStaff.child", "Sibling": "membersStaff.sibling", "Grandparent": "memberWorkflows.profile.grandparent", "Grandchild": "memberWorkflows.profile.grandchild", "Guardian": "membersStaff.guardian", "Dependant": "memberWorkflows.profile.dependant", "Label": "memberWorkflows.profile.label", "Address type": "memberWorkflows.profile.addressType", "Address line 1": "memberWorkflows.profile.addressLine1", "Address line 2": "memberWorkflows.profile.addressLine2", "Town or city": "memberWorkflows.profile.townOrCity", "County, state or region": "memberWorkflows.profile.countyStateOrRegion", "Postcode or ZIP code": "memberWorkflows.profile.postcodeOrZIPCode", "Country": "memberWorkflows.profile.country", "Registration / licence plate": "memberWorkflows.profile.registrationLicencePlate", "Make": "memberWorkflows.profile.make", "Model": "memberWorkflows.profile.model", "Colour": "memberWorkflows.profile.colour", "Vehicle type": "memberWorkflows.profile.vehicleType", "Full name": "memberWorkflows.profile.fullName", "Relationship": "memberWorkflows.profile.relationship", "Mobile number": "memberWorkflows.profile.mobileNumber", "Alternative telephone": "memberWorkflows.profile.alternativeTelephone", "Email address": "memberWorkflows.profile.emailAddress", "Vegetarian": "membersStaff.vegetarian", "Vegan": "membersStaff.vegan", "Pescatarian": "membersStaff.pescatarian", "Gluten-free": "membersStaff.glutenFree", "Dairy-free": "membersStaff.dairyFree", "Halal": "membersStaff.halal", "Kosher": "membersStaff.kosher", "Nut allergy": "membersStaff.nutAllergy", "Other allergy": "membersStaff.otherAllergy", "Other dietary requirement": "memberWorkflows.profile.otherDietaryRequirement", "Recreational": "memberWorkflows.profile.recreational", "Social": "memberWorkflows.profile.social", "Club": "memberWorkflows.profile.club", "Competitive": "memberWorkflows.profile.competitive", "Elite": "memberWorkflows.profile.elite"}
const PERSONAL_DETAIL_SECTIONS = ['Personal','Contact','Addresses','Emergency','Dietary','Vehicles','Family']
const PERSONAL_TAB_KEYS = {"Personal": "membersStaff.personal", "Contact": "membersStaff.contact", "Addresses": "membersStaff.addresses", "Emergency": "membersStaff.emergency", "Dietary": "membersStaff.dietary", "Vehicles": "membersStaff.vehicles", "Family": "membersStaff.family"}
const EMPTY_ADDRESS = { label: 'Home', address_type: 'postal', address_line_1: '', address_line_2: '', city: '', region: '', postal_code: '', country_code: 'GB', is_primary_postal: false }
const FIELD_LABELS = { label: 'Label', address_type: 'Address type', address_line_1: 'Address line 1', address_line_2: 'Address line 2', city: 'Town or city', region: 'County, state or region', postal_code: 'Postcode or ZIP code', country_code: 'Country', registration: 'Registration / licence plate', make: 'Make', model: 'Model', colour: 'Colour', vehicle_type: 'Vehicle type' }
const COUNTRIES = [['GB','United Kingdom'], ['US','United States'], ['IE','Ireland'], ['FR','France'], ['ES','Spain'], ['PT','Portugal'], ['AU','Australia'], ['CA','Canada']]
const ADDRESS_TYPES = ['Home','Work','Holiday Home','Postal','Other']
const VEHICLE_TYPES = ['Car','Motorcycle','Van','Golf buggy','Other']
const EMPTY_VEHICLE = { registration: '', make: '', model: '', colour: '', vehicle_type: 'Car', is_primary: false }
function Field({ label, children, hint, error, id, nativeDate = false }) { const { t } = usePortalPresentationI18n(); error = renderMemberMessage(t, error); const describedBy = error && id ? `${id}-error` : undefined; const enhancedChild = React.isValidElement(children) ? React.cloneElement(children, { id, 'aria-label': label, 'aria-invalid': Boolean(error), 'aria-describedby': describedBy, className: children.type === 'select' ? ['mp-premium-select', children.props.className].filter(Boolean).join(' ') : children.props.className }) : children; return <label className={nativeDate ? 'mp-personal-field mp-personal-field--native-date' : 'mp-personal-field'}><span>{label}</span>{enhancedChild}{hint ? <small>{hint}</small> : null}{error ? <strong id={describedBy} className="mp-field-error">{error}</strong> : null}</label> }
function contactDraft(contact = {}) { return { contact_email: contact.contact_email || '', secondary_email: contact.secondary_email || '', phone_number: contact.phone_number || '', home_phone: contact.home_phone || '', work_phone: contact.work_phone || '' } }
function sectionDraft(data, name) {
  if (name === 'Contact') return contactDraft(data?.contact)
  const section = { ...(data?.[name.toLowerCase()] || {}) }
  if (name === 'Personal') {
    section.date_of_birth = normalizeDateOnly(section.date_of_birth) || ''
    section.anniversary_date = normalizeDateOnly(section.anniversary_date) || ''
  }
  return section
}

export function serializePersonalDraft(draft) {
  return { ...draft, date_of_birth: normalizeDateOnly(draft?.date_of_birth), anniversary_date: normalizeDateOnly(draft?.anniversary_date) }
}

function normalizePersonalProfilePayload(responsePayload, expectedMemberId) {
  const payload = responsePayload
  const profile = payload?.ok === true ? payload.data : payload?.data
  return profile && typeof profile.personal === 'object' && typeof profile.contact === 'object' && (!expectedMemberId || !profile.member?.id || profile.member.id === expectedMemberId) ? profile : null
}

async function readMemberPortalJson(response) {
  if (response && typeof response.json === 'function') return response.json()
  return response
}
function useUnsavedChangesGuard(isDirty) {
  const confirm = useConfirm()
  return useCallback(async (action) => {
    if (!isDirty) { action(); return }
    const approved = await confirm({ title: memberMessage("memberWorkflows.profile.discardChanges"), description: memberMessage("memberWorkflows.profile.youHaveUnsavedChanges"), detail: memberMessage("memberWorkflows.profile.ifYouContinueYourChangesWillBeLost"), confirmLabel: memberMessage("memberWorkflows.profile.discard"), cancelLabel: memberMessage("racquetPlay.keepEditing"), tone: 'danger' })
    if (approved) action()
  }, [confirm, isDirty])
}

function PersonalDetailsEditor({ clubCode, memberPortalApiGet, memberPortalApiFetch, onProfileChange, onDirtyChange, isMobileApp, personalResource, onRefreshSummary, memberId }) {
  const { t: tx } = usePortalPresentationI18n()
  const { t: portalText } = usePortalPresentationI18n()

  const { locale } = usePrivanaI18n()
  const cached = isMobileApp ? personalResource.current : null
  const savingRef = useRef(false)
  const draftDirtyRef = useRef(false)
  const generation = useRef(0)
  const activeRef = useRef('Personal')
  const [refreshFailed, setRefreshFailed] = useState(false)
  const [data,setData]=useState(cached), [active,setActive]=useState('Personal'), [draft,setDraft]=useState(() => sectionDraft(cached, 'Personal')), [dirty,setDirty]=useState(false), [childDirty,setChildDirty]=useState(false), [saving,setSaving]=useState(false), [message,setMessage]=useState(''), [loadError,setLoadError]=useState(''), [isLoading,setIsLoading]=useState(true)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const tabStripRef = useRef(null)
  const hasUnsavedChanges = dirty || childDirty
  const confirmNavigation = useUnsavedChangesGuard(hasUnsavedChanges)
  activeRef.current = active
  draftDirtyRef.current = hasUnsavedChanges
  const acceptData = (next) => { setData(next); personalResource.current = next }
  const load=useCallback(async({ signal }={})=>{
    const request = ++generation.current
    setIsLoading(true); setLoadError('')
    try {
      const r=await memberPortalApiGet(`/api/members-portal/${clubCode}/me/personal-profile${cached ? `?revision=${nextProfileReadRevision()}` : ''}`)
      const payload=await readMemberPortalJson(r)
      const next=normalizePersonalProfilePayload(payload, memberId)
      if(signal?.aborted || generation.current !== request) return
      if(r?.ok === false || !next) throw Object.assign(new Error(), { publicMessage: profileErrorDescriptor(payload, r?.status, locale, 'load') })
      // A late background read must never replace edits or a confirmed write.
      if (!draftDirtyRef.current) { acceptData(next); setDraft(sectionDraft(next, activeRef.current)) }
    } catch(error) {
      if(signal?.aborted || generation.current !== request) return
      setLoadError(error?.publicMessage || profileMessageDescriptor('load', locale))
    } finally { if(!signal?.aborted && generation.current === request) setIsLoading(false) }
  },[memberPortalApiGet,clubCode])
  useEffect(()=>{ const controller = new AbortController(); load({ signal: controller.signal }); return()=>controller.abort() },[load])
  useEffect(()=>{ onDirtyChange?.(hasUnsavedChanges); return()=>onDirtyChange?.(false) },[hasUnsavedChanges,onDirtyChange])
  useEffect(()=>{ const before=(e)=>{ if(hasUnsavedChanges){ e.preventDefault(); e.returnValue='' } }; window.addEventListener('beforeunload',before); return()=>window.removeEventListener('beforeunload',before)},[hasUnsavedChanges])
  useEffect(()=>{
    const strip = tabStripRef.current
    if (!strip?.closest('.mp-app--mobile')) return
    const tab = strip.querySelector('[role="tab"][aria-selected="true"]')
    if (!tab) return
    const stripRect = strip.getBoundingClientRect(), tabRect = tab.getBoundingClientRect()
    const delta = tabRect.left < stripRect.left ? tabRect.left - stripRect.left : tabRect.right > stripRect.right ? tabRect.right - stripRect.right : 0
    if (!delta) return
    const left = strip.scrollLeft + delta
    if (typeof strip.scrollTo === 'function') strip.scrollTo({ left, behavior: 'smooth' })
    else strip.scrollLeft = left
  },[active])
  const [fieldErrors,setFieldErrors]=useState({})
  const switchSection=(name)=>{ confirmNavigation(() => { setActive(name); setDirty(false); setChildDirty(false); setMessage(''); setFieldErrors({}); setDraft(sectionDraft(data, name)) }, 'Discard unsaved changes and switch section?') }
  const set=(k,v)=>{ setDraft(previous=>{
    const next = {...previous,[k]:v}
    const baseline = sectionDraft(data, active)
    setDirty(Object.keys({...baseline,...next}).some(key => (baseline[key] ?? '') !== (next[key] ?? '')))
    return next
  }) }
  const finishSave = async (next) => {
    acceptData(next)
    setFieldErrors({})
    setMessage(profileMessageDescriptor('saved', locale))
    setRefreshFailed(false)
    // The write has committed. A summary read is a separate operation.
    try {
      if (await onProfileChange?.(next.member) === false) {
        setRefreshFailed(true)
        emitMemberClientTelemetry({ event: 'members_profile_refresh_after_save_failed', endpointKey: 'personal_profile', status: 0, errorCode: 'saved_refresh_failed' })
      }
    } catch { setRefreshFailed(true) }
  }
  const saveSection=async()=>{
    if (savingRef.current || !dirty) return
    savingRef.current = true; generation.current += 1; setIsLoading(false)
    setSaving(true); setMessage('')
    try {
      const requestPayload = active === 'Personal' ? serializePersonalDraft(draft) : draft
      const r=await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/personal-profile/${active.toLowerCase()}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify(requestPayload)})
      const payload=await r.json()
      if(!mounted.current) return
      if(r.ok === false || !payload.ok || !normalizePersonalProfilePayload(payload, memberId)) {
        const error = profileErrorDescriptor(payload, r.status, locale)
        setFieldErrors(payload.field ? {[payload.field]: error} : {})
        setMessage(error); return
      }
      setDraft(sectionDraft(payload.data, active)); setDirty(false)
      await finishSave(payload.data)
    } catch { if(mounted.current) setMessage(profileMessageDescriptor('save', locale)) }
    finally { savingRef.current = false; if(mounted.current) setSaving(false) }
  }
  const postChild=async(kind,item,id=null,del=false)=>{
    if (savingRef.current) return false
    savingRef.current = true; generation.current += 1; setIsLoading(false); setSaving(true)
    try {
      const url=`/api/members-portal/${clubCode}/me/${kind}${id?`/${id}`:''}`
      const r=await memberPortalApiFetch(url,{method: del?'DELETE':(id?'PATCH':'POST'),headers:{'Content-Type':'application/json'},body:del?undefined:JSON.stringify(item)})
      const payload=await r.json()
      if(!mounted.current) return false
      if(r.ok === false || !payload.ok || !normalizePersonalProfilePayload(payload, memberId)) {
        const error = profileErrorDescriptor(payload, r.status, locale)
        setFieldErrors(payload.field ? {[payload.field]: error} : {}); setMessage(error); return false
      }
      await finishSave(payload.data); return true
    } catch { if(mounted.current) setMessage(profileMessageDescriptor('save', locale)); return false }
    finally { savingRef.current = false; if(mounted.current) setSaving(false) }
  }
  if(isLoading && !data && isMobileApp) return <section className="mp-personal-details-workspace" aria-busy="true" aria-label={locale === 'es' ? 'Cargando datos personales' : 'Loading personal details'}>
    <div className="mp-personal-section-bar mp-personal-skeleton-tabs" aria-hidden="true">{[1,2,3,4].map(i=><span key={i} />)}</div>
    <div className="mp-personal-card" aria-hidden="true"><div className="mp-personal-grid">{Array.from({length:9},(_,i)=><div className="mp-personal-skeleton-field" key={i}><span/><i/></div>)}</div></div>
    <span className="sr-only" role="status">{locale === 'es' ? 'Cargando datos personales…' : 'Loading personal details…'}</span>
  </section>
  if(isLoading && !data) return <section className="mp-personal-details-workspace mp-personal-details-workspace--state" aria-busy="true"><div className="mp-personal-state-card" role="status"><span className="mp-loading-dot" aria-hidden="true" />{portalText("memberRemaining.m0")}</div></section>
  if(loadError && !data) return <section className="mp-personal-details-workspace mp-personal-details-workspace--state"><div className="mp-personal-state-card mp-personal-state-card--error" role="alert"><strong>{locale === 'es' ? 'No se pueden cargar los datos personales' : 'Unable to load personal details'}</strong><p>{renderMemberMessage(tx, loadError)}</p><button type="button" className="mp-primary" onClick={()=>load()}>{locale === 'es' ? 'Reintentar' : 'Retry'}</button></div></section>
  if(!data) return null
  const activeSlug = active.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const activeTabId = `personal-details-tab-${activeSlug}`
  const activePanelId = `personal-details-panel-${activeSlug}`
  return <section className="mp-personal-details-workspace" aria-busy={isLoading} aria-labelledby={activeTabId}>
    {loadError ? <p role="alert">{renderMemberMessage(tx, loadError)} <button type="button" onClick={()=>load()}>{locale === 'es' ? 'Reintentar' : 'Retry'}</button></p> : null}
    {refreshFailed ? <p role="status">{renderMemberMessage(tx, profileMessageDescriptor('refresh'))} <button type="button" onClick={async()=>{ if(await onRefreshSummary?.() !== false) setRefreshFailed(false) }}>{renderMemberMessage(tx, profileMessageDescriptor('retry'))}</button></p> : null}
    <div ref={tabStripRef} className="mp-personal-section-bar" role="tablist" aria-label={tx("memberWorkflows.profile.personalDetailsSections")}>{PERSONAL_DETAIL_SECTIONS.map(name=>{ const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-'); return <button key={name} id={`personal-details-tab-${slug}`} type="button" role="tab" disabled={saving} aria-controls={`personal-details-panel-${slug}`} aria-selected={active===name} className={active===name?'is-active':''} onClick={()=>switchSection(name)}>{portalText(PERSONAL_TAB_KEYS[name])}</button> })}</div>
    <div id={activePanelId} className="mp-personal-card" role="tabpanel" aria-labelledby={activeTabId}><fieldset disabled={saving} className="mp-personal-save-fields">{message?<p className="mp-save-message" role="status" aria-live="polite">{renderMemberMessage(tx, message)}</p>:null}{Object.keys(fieldErrors).length?<div className="mp-error-summary" role="alert"><strong>{portalText("memberRemaining.m1")}</strong></div>:null}
    {active==='Personal'&&<div className="mp-personal-grid"><Field label={portalText("membersStaff.preferredTitle")}><select value={draft.preferred_title||''} onChange={e=>set('preferred_title',e.target.value)}><option value="">{portalText("epos.copy16")}</option>{['Mr','Mrs','Miss','Ms','Dr','Prof','Sir','Dame','Lord','Lady','Rev','Other'].map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[x])}</option>)}</select></Field><Field label={portalText("membersStaff.firstName")}><input autoComplete="given-name" value={draft.first_name||''} onChange={e=>set('first_name',e.target.value)}/></Field><Field label={tx("memberWorkflows.profile.middleNameS")}><input value={draft.middle_names||''} onChange={e=>set('middle_names',e.target.value)}/></Field><Field label={portalText("membersStaff.lastName")}><input autoComplete="family-name" value={draft.last_name||''} onChange={e=>set('last_name',e.target.value)}/></Field><Field label={tx("memberWorkflows.profile.preferredDisplayName")}><input value={draft.preferred_name||''} onChange={e=>set('preferred_name',e.target.value)}/></Field><Field label={portalText("membersStaff.gender")}><select value={draft.gender||''} onChange={e=>set('gender',e.target.value)}><option value="">{portalText("epos.copy16")}</option>{['Female','Male','Non-binary','Prefer not to say','Other'].map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[x])}</option>)}</select></Field><Field id="date_of_birth" nativeDate error={fieldErrors.date_of_birth} label={portalText("membersStaff.dateOfBirth")}><input className="mp-native-date-input" type="date" value={draft.date_of_birth?.slice?.(0,10)||''} onChange={e=>set('date_of_birth',e.target.value)}/></Field><Field label={portalText("membersStaff.maritalStatus")}><select value={draft.marital_status||''} onChange={e=>set('marital_status',e.target.value)}><option value="">{portalText("epos.copy16")}</option>{['Single','Married','Civil partnership','Partnered','Separated','Divorced','Widowed','Prefer not to say'].map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[x])}</option>)}</select></Field><Field id="anniversary_date" nativeDate error={fieldErrors.anniversary_date} label={portalText("membersStaff.anniversary")} hint={tx("memberWorkflows.profile.leaveBlankIfNotApplicable")}><input className="mp-native-date-input" type="date" value={draft.anniversary_date?.slice?.(0,10)||''} onChange={e=>set('anniversary_date',e.target.value)}/></Field></div>}
    {active==='Contact'&&<div className="mp-personal-grid"><div className="mp-readonly-field"><span>{portalText("membersStaff.signInEmail")}</span><strong>{data.contact.sign_in_email || tx("profileOverview.unavailable")}</strong><small>{portalText("memberRemaining.m2")}</small></div><Field id="contact_email" error={fieldErrors.contact_email} label={portalText("membersStaff.contactEmail")} ><input type="email" autoComplete="email" value={draft.contact_email||''} onChange={e=>set('contact_email',e.target.value)}/></Field><Field id="secondary_email" error={fieldErrors.secondary_email} label={portalText("membersStaff.secondaryEmail")}><input type="email" value={draft.secondary_email||''} onChange={e=>set('secondary_email',e.target.value)}/></Field><Field id="phone_number" error={fieldErrors.phone_number} label={tx("memberWorkflows.profile.mobileNumber")}><input type="tel" autoComplete="tel" value={draft.phone_number||''} onChange={e=>set('phone_number',e.target.value)}/></Field><Field label={portalText("membersStaff.homeTelephone")}><input type="tel" value={draft.home_phone||''} onChange={e=>set('home_phone',e.target.value)}/></Field><Field label={portalText("membersStaff.workTelephone")}><input type="tel" value={draft.work_phone||''} onChange={e=>set('work_phone',e.target.value)}/></Field></div>}
    {active==='Emergency'&&<div className="mp-personal-grid"><p className="mp-privacy-note">{portalText("memberRemaining.m3")}</p>{[['emergency_contact_name','Full name'],['emergency_contact_relationship','Relationship'],['emergency_contact_mobile','Mobile number'],['emergency_contact_phone','Alternative telephone'],['emergency_contact_email','Email address']].map(([k,l])=><Field key={k} id={k} error={fieldErrors[k]} label={tx(PROFILE_ENUM_KEYS[l])}><input type={k.includes('email')?'email':k.includes('phone')||k.includes('mobile')?'tel':'text'} value={draft[k]||''} onChange={e=>set(k,e.target.value)}/></Field>)}</div>}
    {active==='Dietary'&&<div><div className="mp-checkbox-grid">{[['dietary_vegetarian','Vegetarian'],['dietary_vegan','Vegan'],['dietary_pescatarian','Pescatarian'],['dietary_gluten_free','Gluten-free'],['dietary_dairy_free','Dairy-free'],['dietary_halal','Halal'],['dietary_kosher','Kosher'],['dietary_nut_allergy','Nut allergy'],['dietary_other_allergy','Other allergy'],['dietary_other_requirement','Other dietary requirement']].map(([k,l])=><label className="mp-dietary-option" key={k}><input type="checkbox" checked={!!draft[k]} onChange={e=>set(k,e.target.checked)}/><span className="mp-dietary-checkbox" aria-hidden="true"><Check size={15}/></span><span className="mp-dietary-option__label">{tx(PROFILE_ENUM_KEYS[l])}</span></label>)}</div><Field label={tx("memberWorkflows.profile.allergyDetails")}><textarea value={draft.dietary_allergy_details||''} onChange={e=>set('dietary_allergy_details',e.target.value)} /></Field><Field label={tx("memberWorkflows.profile.dietaryLifestyleNotes")}><textarea value={draft.dietary_notes||''} onChange={e=>set('dietary_notes',e.target.value)} /></Field></div>}
    {active==='Addresses'&&<ChildList items={data.addresses} empty={EMPTY_ADDRESS} fields={['label','address_type','address_line_1','address_line_2','city','region','postal_code','country_code']} primary="is_primary_postal" onSave={(item,id)=>postChild('addresses',item,id)} onDelete={(id)=>postChild('addresses',{},id,true)} onDirtyChange={setChildDirty}/>}
    {active==='Vehicles'&&<ChildList items={data.vehicles} empty={EMPTY_VEHICLE} fields={['registration','make','model','colour','vehicle_type']} primary="is_primary" onSave={(item,id)=>postChild('vehicles',item,id)} onDelete={(id)=>postChild('vehicles',{},id,true)} onDirtyChange={setChildDirty}/>}
    {active==='Family'&&<FamilyManager clubCode={clubCode} data={data} memberPortalApiGet={memberPortalApiGet} memberPortalApiFetch={memberPortalApiFetch} onRefresh={(next)=>{acceptData(next); setDraft(sectionDraft(next, active)); setMessage(memberMessage("memberWorkflows.profile.updated"))}} />}
    {['Personal','Contact','Emergency','Dietary'].includes(active)&&<div className="mp-form-actions"><button type="button" className="mp-ghost" disabled={!dirty||saving} onClick={()=>{setDraft(sectionDraft(data, active));setDirty(false)}}>{portalText("roomScreen.cancel")}</button><button type="button" className="mp-primary" disabled={!dirty||saving} onClick={saveSection}>{saving?tx("golfStats.saving"):tx("memberWorkflows.profile.saveChanges")}</button></div>}
    </fieldset></div></section>
}

function FamilyManager({ clubCode, data, memberPortalApiGet, memberPortalApiFetch, onRefresh }) {
  const { t: tx } = usePortalPresentationI18n()
  const { t: portalText } = usePortalPresentationI18n()

  const { locale } = usePrivanaI18n()
  const confirm = useConfirm()
  const [adding,setAdding]=useState(false), [q,setQ]=useState(''), [results,setResults]=useState([]), [selected,setSelected]=useState(null), [relation,setRelation]=useState('spouse'), [busy,setBusy]=useState(''), [error,setError]=useState(''), [searching,setSearching]=useState(false), [searchError,setSearchError]=useState('')
  const relationOptions=['spouse','partner','parent','child','sibling','grandparent','grandchild','guardian','dependant','other']
  useEffect(()=>{ if(!adding || q.trim().length<2){setResults([]);setSearching(false);setSearchError('');return}; setSearching(true); setSearchError(''); const t=setTimeout(async()=>{ try{ const r=await memberPortalApiGet(`/api/members-portal/${clubCode}/me/family-search?q=${encodeURIComponent(q)}&limit=8`); const p=await readMemberPortalJson(r); if(!p.ok) throw Object.assign(new Error(), { publicMessage: profileErrorDescriptor(p, r.status, locale) }); setResults(Array.isArray(p.data)?p.data:[]) }catch(e){ setResults([]); setSearchError(e?.publicMessage || profileMessageDescriptor('load', locale)) } finally { setSearching(false) } },250); return()=>clearTimeout(t)},[adding,q,clubCode,memberPortalApiGet])
  const submit=async()=>{ if(!selected) return; setBusy('submit'); setError(''); try{ const r=await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/family-requests`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({request_type:'add',related_member_id:selected.id,relation})}); const p=await r.json(); if(!p.ok) throw Object.assign(new Error(), { publicMessage: profileErrorDescriptor(p, r.status, locale) }); onRefresh(p.data); setAdding(false); setQ(''); setSelected(null) }catch(e){ setError(e?.publicMessage || profileMessageDescriptor('save', locale)) }finally{ setBusy('') } }
  const respond=async(id,action)=>{ setBusy(`${id}:${action}`); setError(''); try{ const r=await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/family-requests/${id}/respond`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action})}); const p=await r.json(); if(!p.ok) throw Object.assign(new Error(), { publicMessage: profileErrorDescriptor(p, r.status, locale) }); onRefresh(p.data) }catch(e){ setError(e?.publicMessage || profileMessageDescriptor('save', locale)) }finally{ setBusy('') } }
  const removeRelationship=async(f)=>{ const approved = await confirm({ title: memberMessage("memberWorkflows.profile.removeFamilyRelationship"), description: memberMessage("memberWorkflows.profile.thisWillRemoveTheFamilyRelationshipForBothMembers"), detail: memberMessage("memberWorkflows.profile.thisActionCannotBeUndoneAutomatically"), confirmLabel: memberMessage("membersStaff.remove"), cancelLabel: memberMessage("memberWorkflows.profile.keepRelationship"), tone: 'danger' }); if(!approved) return; setBusy(`family:${f.id}`); setError(''); try{ const r=await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/family/${f.related_member_id}`,{method:'DELETE'}); const p=await r.json(); if(!p.ok) throw Object.assign(new Error(), { publicMessage: profileErrorDescriptor(p, r.status, locale) }); onRefresh(p.data) }catch(e){ setError(e?.publicMessage || profileMessageDescriptor('save', locale)) }finally{ setBusy('') } }
  const requests=data.familyRequests||[], incoming=requests.filter(r=>r.direction==='incoming'&&r.status==='pending'), outgoing=requests.filter(r=>r.direction==='outgoing'&&r.status==='pending')
  const name=(m)=>[m.preferred_name||m.first_name,m.last_name].filter(Boolean).join(' ')||m.related_member_name||m.display_name||tx("membersStaff.member")
  const initials=(m)=>name(m).split(/\s+/).slice(0,2).map(x=>x[0]).join('').toUpperCase()||'M'
  const memberNumber=(m)=>m.membership_number?tx('profileOverview.number', {number:m.membership_number}):tx("memberWorkflows.profile.memberNumberUnavailable")
  const avatar=(m)=><span className="mp-family-avatar">{m.profile_picture?<img src={m.profile_picture} alt="" />:<span>{initials(m)}</span>}</span>
  const badge=()=><span className="mp-family-status">{tx('memberBilling.status_pending')}</span>
  const searchState=!q.trim()? tx("memberWorkflows.profile.enterAtLeastTwoCharactersToFindAnActive") : q.trim().length<2 ? tx("memberWorkflows.profile.keepTypingToSearchActiveClubMembers") : searching ? tx("memberBilling.searching") : searchError ? searchError : !results.length ? tx("memberWorkflows.profile.noMatchingMembersFound") : ''
  const requestMessage=(r)=>r.request_type==='remove'?tx("memberWorkflows.profile.hasRequestedToRemoveYourFamilyRelationship"):tx("memberWorkflows.profile.hasRequestedToAddYouAsTheirRelation", {relation:tx(PROFILE_ENUM_KEYS[formatRelationshipLabel(r.relation)])})
  return <div className="mp-family-manager">
    <section className="mp-family-section mp-family-action-group" aria-labelledby="family-add-title"><div className="mp-child-list__header"><div><h5 id="family-add-title">{portalText("memberRemaining.m4")}</h5><p>{portalText("memberRemaining.m5")}</p></div><button className="mp-primary" type="button" onClick={()=>setAdding(!adding)}><UserPlus size={16}/> {portalText("memberRemaining.m4")}</button></div></section>
    {error?<p className="mp-save-message mp-family-error" role="alert">{renderMemberMessage(tx, error)}</p>:null}
    {adding?<div className="mp-child-editor mp-family-search-panel"><Field label={tx("memberWorkflows.profile.searchActiveClubMembers")}><input value={q} onChange={e=>{setQ(e.target.value);setSelected(null)}} placeholder={tx("memberWorkflows.profile.nameOrMembershipNumber")} /></Field><div className="mp-family-results" aria-live="polite">{searchState?<p className={`mp-family-search-state${searchError?' is-error':''}`}>{renderMemberMessage(tx, searchState)}</p>:null}{results.map(r=><button type="button" key={r.id} className={selected?.id===r.id?'is-selected':''} onClick={()=>setSelected(r)}>{avatar(r)}<span className="mp-family-result-copy"><strong>{name(r)}</strong><small>{memberNumber(r)}</small></span></button>)}</div>{selected?<div className="mp-family-selected-state"><span>{portalText("memberRemaining.m6")}</span><strong>{name(selected)}</strong><small>{memberNumber(selected)}</small></div>:null}{selected?<Field label={tx("memberWorkflows.profile.selectedMemberIsMy")}><select className="mp-premium-select" value={relation} onChange={e=>setRelation(e.target.value)}>{relationOptions.map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[formatRelationshipLabel(x)])}</option>)}</select></Field>:null}<button className="mp-primary" type="button" disabled={!selected||!!busy} onClick={submit}>{busy==='submit'?tx("memberWorkflows.profile.sending"):tx("memberWorkflows.profile.sendRequest")}</button></div>:null}
    <section className="mp-family-section"><h5>{portalText("memberRemaining.m7")}</h5><div className="mp-family-card-grid">{(data.family||[]).length?data.family.map(f=><article className="mp-family-card mp-family-card--relationship" key={f.id}>{avatar(f)}<div className="mp-family-card__main"><div className="mp-family-card__heading"><strong>{name(f)}</strong><button className="mp-family-action mp-family-action--secondary mp-ghost" type="button" onClick={()=>removeRelationship(f)} disabled={!!busy}>{busy===`family:${f.id}`?tx("memberWorkflows.profile.removing"):tx("membersStaff.removal")}</button></div><small>{memberNumber(f)}</small><p><span className="mp-family-relation-badge">{tx(PROFILE_ENUM_KEYS[formatRelationshipLabel(f.relation)])}</span></p></div></article>):<div className="mp-family-empty"><UserRound size={18} aria-hidden="true"/><div><strong>{portalText("memberRemaining.m8")}</strong><p>{portalText("memberRemaining.m9")}</p></div></div>}</div></section>
    {incoming.length?<section className="mp-family-section"><h5>{portalText("memberRemaining.m10")}</h5><div className="mp-family-card-grid">{incoming.map(r=><article className="mp-family-card mp-family-request-card" key={r.id}>{avatar(r)}<div className="mp-family-card__main"><strong>{name(r)}</strong><small>{memberNumber(r)}</small><p>{requestMessage(r)}</p></div>{badge()}<div className="mp-family-card__actions"><button className="mp-family-action mp-family-action--primary mp-primary" type="button" onClick={()=>respond(r.id,'accept')} disabled={!!busy}>{busy===`${r.id}:accept`?(r.request_type==='remove'?tx("memberWorkflows.profile.confirming"):tx("memberWorkflows.profile.accepting")):(r.request_type==='remove'?tx("memberWorkflows.profile.confirmRemoval"):tx("memberWorkflows.profile.acceptRelationship"))}</button><button className="mp-family-action mp-family-action--secondary mp-ghost" type="button" onClick={()=>respond(r.id,'reject')} disabled={!!busy}>{busy===`${r.id}:reject`?(r.request_type==='remove'?tx("memberWorkflows.profile.keeping"):tx("memberWorkflows.profile.rejecting")):(r.request_type==='remove'?tx("memberWorkflows.profile.keepRelationship"):tx("memberWorkflows.profile.reject"))}</button></div></article>)}</div></section>:null}
    {outgoing.length?<section className="mp-family-section"><h5>{portalText("memberRemaining.m11")}</h5><div className="mp-family-card-grid">{outgoing.map(r=><article className="mp-family-card mp-family-request-card" key={r.id}>{avatar(r)}<div className="mp-family-card__main"><strong>{name(r)}</strong><small>{memberNumber(r)}</small><p>{r.request_type==='remove'?tx("memberWorkflows.profile.removalRequest"):tx("memberWorkflows.profile.relationRelationshipRequest", {relation:tx(PROFILE_ENUM_KEYS[formatRelationshipLabel(r.relation)])})}</p></div>{badge()}<div className="mp-family-card__actions"><button className="mp-family-action mp-family-action--secondary mp-ghost" type="button" onClick={()=>respond(r.id,'cancel')} disabled={!!busy}>{busy===`${r.id}:cancel`?tx("memberWorkflows.profile.cancelling"):tx("memberWorkflows.profile.cancelRequest")}</button></div></article>)}</div></section>:null}
  </div>
}

function ChildList({items=[],empty,fields,primary,onSave,onDelete,onDirtyChange}){
  const { t: tx, locale } = usePortalPresentationI18n()
  const { t: portalText } = usePortalPresentationI18n()

  const confirm = useConfirm()
  const [edit,setEdit]=useState(null); const [childDirty,setChildDirty]=useState(false); const [childSaving,setChildSaving]=useState(false); const [deleteError,setDeleteError]=useState('')
  useEffect(()=>{onDirtyChange?.(childDirty); return()=>onDirtyChange?.(false)},[childDirty,onDirtyChange])
  const start=(item={...empty})=>{setEdit(item);setChildDirty(false);setDeleteError('')}; const update=(f,v)=>{setEdit({...edit,[f]:v});setChildDirty(true)}; const close=()=>{setEdit(null);setChildDirty(false)}
  const save=async()=>{ if(childSaving) return; setChildSaving(true); try{ const ok=await onSave(edit,edit.id); if(ok) close() } finally{ setChildSaving(false) } }
  const remove=async(item)=>{ if(childSaving) return; const isVehicle=fields.includes('registration'); const approved=await confirm({ title: isVehicle?memberMessage("memberWorkflows.profile.removeVehicle"):memberMessage("memberWorkflows.profile.removeAddress"), description: isVehicle?memberMessage("memberWorkflows.profile.thisVehicleWillBeRemovedFromYourProfile"):memberMessage("memberWorkflows.profile.thisAddressWillBeRemovedFromYourProfile"), confirmLabel:memberMessage("membersStaff.remove"), cancelLabel:memberMessage("roomScreen.cancel"), tone:'danger' }); if(!approved) return; setChildSaving(true); setDeleteError(''); try{ const ok=await onDelete(item.id); if(!ok) setDeleteError(memberMessage(isVehicle ? "memberWorkflows.profile.unableToRemoveVehicle" : "memberWorkflows.profile.unableToRemoveAddress")) } finally{ setChildSaving(false) } }
  const isVehicle=fields.includes('registration')
  return <div className={`mp-child-list${isVehicle?' mp-vehicle-list':''}`}>
    <div className="mp-child-list__header"><div><h5>{isVehicle?'Vehicles':'Addresses'}</h5>{isVehicle?<p>{portalText("memberRemaining.m12")}</p>:null}</div><button className="mp-primary" type="button" onClick={()=>start()} disabled={childSaving}>{isVehicle?<Plus size={16}/>:null}{isVehicle?tx("memberWorkflows.profile.addVehicle"):tx("memberWorkflows.profile.addAddress")}</button></div>
    {deleteError?<p className="mp-save-message" role="alert">{renderMemberMessage(tx, deleteError)}</p>:null}
    {edit&&<div className="mp-child-editor mp-child-editor--premium">{fields.map(f=><Field key={f} id={f} label={tx(PROFILE_ENUM_KEYS[FIELD_LABELS[f]])}>{f==='country_code'?<select value={edit[f]||'GB'} onChange={e=>update(f,e.target.value)}>{COUNTRIES.map(([code,name])=><option key={code} value={code}>{new Intl.DisplayNames([locale], { type: 'region' }).of(code)}</option>)}</select>:f==='address_type'?<select value={edit[f]||'Home'} onChange={e=>update(f,e.target.value)}>{ADDRESS_TYPES.map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[x])}</option>)}</select>:f==='vehicle_type'?<select value={edit[f]||'Car'} onChange={e=>update(f,e.target.value)}>{VEHICLE_TYPES.map(x=><option key={x} value={x}>{tx(PROFILE_ENUM_KEYS[x])}</option>)}</select>:<input value={edit[f]||''} onChange={e=>update(f,e.target.value)}/>}</Field>)}<label className="mp-checkbox-premium"><input type="checkbox" checked={!!edit[primary]} onChange={e=>update(primary,e.target.checked)}/> {isVehicle?tx("membersStaff.primaryVehicle"):tx("memberWorkflows.profile.mainPostalAddress")}</label><div className="mp-child-editor__actions"><button className="mp-primary" type="button" onClick={save} disabled={childSaving}>{childSaving?tx("golfStats.saving"):isVehicle?tx("memberWorkflows.profile.saveVehicle"):tx("memberWorkflows.profile.saveAddress")}</button><button className="mp-ghost" type="button" onClick={close} disabled={childSaving}>{portalText("roomScreen.cancel")}</button></div></div>}
    <div className="mp-family-card-grid">{items.map(item=><article className={`mp-profile-item-card${isVehicle?' mp-vehicle-card':''}`} key={item.id}><span className="mp-profile-item-card__icon" aria-hidden="true">{isVehicle?<Car size={20}/>:<IdCard size={20}/>}</span><div className="mp-profile-item-card__main"><div className="mp-profile-item-card__heading"><strong>{item.registration || item.label || item.address_line_1}</strong>{item[primary]?<span className="mp-family-relation-badge" aria-label={isVehicle?tx("membersStaff.primaryVehicle"):tx("memberWorkflows.profile.mainPostalAddress")}>{isVehicle?tx("memberWorkflows.profile.primary"):tx("memberWorkflows.profile.mainPostalAddress")}</span>:null}</div><p>{isVehicle?[item.make,item.model].filter(Boolean).join(' ') : [item.address_line_1,item.city,item.postal_code,item.country_code].filter(Boolean).join(', ')}</p>{isVehicle?<small>{[item.colour,PROFILE_ENUM_KEYS[item.vehicle_type] ? tx(PROFILE_ENUM_KEYS[item.vehicle_type]) : item.vehicle_type].filter(Boolean).join(' · ')}</small>:null}</div><div className="mp-profile-item-card__actions"><button className="mp-family-action mp-ghost" type="button" onClick={()=>start(item)} disabled={childSaving}><Pencil size={14}/> {portalText("crm.edit")}</button><button className="mp-family-action mp-family-action--secondary mp-ghost" type="button" onClick={()=>remove(item)} disabled={childSaving}>{childSaving?tx("memberWorkflows.profile.removing"):tx("membersStaff.remove")}</button></div></article>)}</div>
  </div>
}

function ProfilePlaceholderSection({ title, description, bullets = [], accent = 'indigo', Icon = ShieldCheck }) {
  const { t: portalText } = usePortalPresentationI18n()

  return (
    <section className="mp-profile-placeholder-panel" aria-labelledby={`profile-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className={`mp-profile-placeholder-panel__icon mp-profile-accent--${accent}`} aria-hidden="true"><Icon size={24} /></div>
      <div className="mp-profile-placeholder-panel__content">
        <p className="mp-profile-placeholder-panel__status">{portalText("epos.copy424")}</p>
        <h4 id={`profile-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{title}</h4>
        <p className="mp-muted">{description}</p>
        {bullets.length ? (
          <div className="mp-profile-placeholder-panel__list">
            <p>{portalText("memberRemaining.m13")}</p>
            <ul>
              {bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function ProfileSectionPageHeader({ section, onBack }) {
  const { t } = usePrivanaI18n()
  const sectionConfig = PROFILE_SECTIONS.find((item) => item.id === section)

  return (
    <div className="mp-profile-section-page-header">
      <button type="button" className="mp-ghost mp-profile-section-back" onClick={onBack}>
        <ArrowLeft size={15} />
        {section === 'achievements' ? t('memberSecurity.back') : section === 'digital-membership-card' ? t('digitalMembershipCard.back') : section === 'security' ? t('memberSecurity.back') : t("roomScreen.back")}
      </button>
      <h3>{section === 'achievements' ? t('profileOverview.tiles.achievements.title') : section === 'digital-membership-card' ? t('digitalMembershipCard.title') : section === 'security' ? t('memberSecurity.navTitle') : sectionConfig ? t(`profileOverview.tiles.${sectionConfig.id}.title`) : t('profileOverview.title')}</h3>
    </div>
  )
}


function MobileProfileLogoutSection({ onLogout, isLoggingOut }) {
  const { t } = usePrivanaI18n()
  const [confirming, setConfirming] = useState(false)
  if (typeof onLogout !== 'function') return null

  const confirmLogout = () => {
    if (isLoggingOut) return
    setConfirming(true)
  }

  return (
    <section className="mp-profile-mobile-logout" aria-label={t('profileOverview.signOut')}>
      <button type="button" className="mp-profile-mobile-logout__button" onClick={confirmLogout} disabled={isLoggingOut}>
        <span className="mp-profile-mobile-logout__icon" aria-hidden="true"><LogOut size={18} /></span>
        <span>{t(isLoggingOut ? 'profileOverview.signingOut' : 'profileOverview.signOut')}</span>
      </button>
      {confirming ? (
        <div className="mp-profile-logout-sheet" role="dialog" aria-modal="true" aria-labelledby="member-profile-logout-title">
          <button type="button" className="mp-profile-logout-sheet__backdrop" aria-label={t('profileOverview.cancelSignOut')} onClick={() => setConfirming(false)} />
          <div className="mp-profile-logout-sheet__panel">
            <h4 id="member-profile-logout-title">{t('profileOverview.signOutTitle')}</h4>
            <p>{t('profileOverview.signOutHelp')}</p>
            <button type="button" className="mp-primary" onClick={onLogout} disabled={isLoggingOut}>{t(isLoggingOut ? 'profileOverview.signingOut' : 'profileOverview.signOut')}</button>
            <button type="button" className="mp-ghost" onClick={() => setConfirming(false)} disabled={isLoggingOut}>{t('profileOverview.cancel')}</button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function ProfileSectionRenderer({ section, sportsSection, clubCode, memberPortalApiGet, memberPortalApiFetch, onProfileChange, onDirtyChange, membershipSection, securitySection, digitalCardSection, achievementsSection, isMobileApp, personalResource, onRefreshSummary, memberId }) {
  const sectionConfig = PROFILE_SECTIONS.find((item) => item.id === section) || PROFILE_SECTIONS[0]
  if (sectionConfig.id === 'personal-details') return <PersonalDetailsEditor memberId={memberId} isMobileApp={isMobileApp} personalResource={personalResource} onRefreshSummary={onRefreshSummary} clubCode={clubCode} memberPortalApiGet={memberPortalApiGet} memberPortalApiFetch={memberPortalApiFetch} onProfileChange={onProfileChange} onDirtyChange={onDirtyChange} />
  if (sectionConfig.id === 'sports-playing-levels') return sportsSection
  if (sectionConfig.id === 'achievements') return achievementsSection
  if (sectionConfig.id === 'security') return securitySection
  if (sectionConfig.id === 'membership-information') return membershipSection
  if (sectionConfig.id === 'digital-membership-card') return digitalCardSection
  return <ProfilePlaceholderSection title={sectionConfig.title} description={sectionConfig.placeholder} bullets={sectionConfig.bullets} accent={sectionConfig.accent} Icon={sectionConfig.icon} />
}

const SPORT_FIELDS = { golf: 'golf_handicap', tennis: 'tennis_rating', padel: 'padel_rating', squash: 'squash_rating', pickleball: 'pickleball_rating' }
function readSportLevels(profile) {
  return Object.fromEntries(Object.entries(SPORT_FIELDS).map(([key, field]) => [key,
    profile?.[field] == null ? null : key === 'golf' ? normalizeGolfValue(profile[field], null) : normalizeRatingValue(profile[field], null),
  ]))
}

function MemberProfilePageContent({ clubCode, member, onMemberProfileChange, onLogout, isLoggingOut = false, isMobileApp = false, identityScope = '', branding }) {
  const { t: tx } = usePortalPresentationI18n()
  const { t: portalText } = usePortalPresentationI18n()

  const [frontDeskOpen, setFrontDeskOpen] = useMembersConversationOverlay()
  const [achievementDetailsOpen,setAchievementDetailsOpen] = useState(false)
  const personalResource = useRef(null)
  const [personalDetailsDirty, setPersonalDetailsDirty] = useState(false)
  const { memberPortalApiGet, memberPortalApiPost, memberPortalApiFetch, memberPortalApiPatch } = useMemberPortalApi()
  const { setLocale, t, locale } = usePrivanaI18n()
  const { toast } = useToast()
  const [profile, setProfile] = useState(member || null)
  const overview = useProfileOverview({ scope: `${identityScope}:${clubCode}:${member?.id || ''}`, clubCode, memberId: member?.id, get: memberPortalApiGet })
  const handlePersonalProfileChange = useCallback((next) => {
    setProfile(previous => ({ ...previous, ...next }))
    onMemberProfileChange?.(next)
    return overview.refresh({ afterSave: true, savedMember: next })
  }, [member, onMemberProfileChange, overview.refresh])
  const [languageSaving, setLanguageSaving] = useState(false)
  const [levels, setLevels] = useState(() => readSportLevels(member))
  const [confirmedLevels, setConfirmedLevels] = useState(() => readSportLevels(member))
  const sportScope = JSON.stringify([identityScope, clubCode, member?.id])
  const sportsMounted = useRef(true)
  const sportRevisions = useRef({})
  const sportEdits = useRef({})
  const [sportReadStatus, setSportReadStatus] = useState('loading')
  const [sportReadRetry, setSportReadRetry] = useState(0)
  useEffect(() => { sportsMounted.current = true; return () => { sportsMounted.current = false } }, [])
  const [expandedSport, setExpandedSport] = useState(null)
  const [saveStates, setSaveStates] = useState({})
  const [golfEntry, setGolfEntry] = useState('')
  const [golfEntryError, setGolfEntryError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [animationState, setAnimationState] = useState(null)
  const [animationLoading, setAnimationLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [videoLoadFailed, setVideoLoadFailed] = useState(false)
  const [portraitMode, setPortraitMode] = useState('photo')
  const [portraitModeSaveError, setPortraitModeSaveError] = useState('')
  const [photoError, setPhotoError] = useState('')
  const [securityParams, setSecurityParams] = useSearchParams()
  const [selectedSection, setSelectedSection] = useState(() => securityParams.get('profileSection') === 'security' ? 'security' : null)
  useEffect(() => {
    setSelectedSection(previous => securityParams.get('profileSection') === 'security' ? 'security' : previous === 'security' ? null : previous)
  }, [securityParams])
  const profilePageMode = selectedSection === 'achievements' ? 'achievements' : selectedSection === 'digital-membership-card' ? 'digital-membership-card' : selectedSection === 'security' ? 'security' : selectedSection === 'membership-information' ? 'membership-information' : selectedSection === 'personal-details' ? 'personal-details' : selectedSection === 'sports-playing-levels' ? 'sports-playing-levels' : 'overview'
  const confirmPersonalDetailsNavigation = useUnsavedChangesGuard(personalDetailsDirty && selectedSection === 'personal-details')
  const handleSelectSection = useCallback((sectionId) => {
    confirmPersonalDetailsNavigation(() => {
      if (sectionId === 'security') { const next = new URLSearchParams(securityParams); next.set('profileSection', 'security'); setSecurityParams(next) }
      setSelectedSection(sectionId)
      setPersonalDetailsDirty(false)
    }, 'Discard unsaved changes and open another profile section?')
  }, [confirmPersonalDetailsNavigation, securityParams, setSecurityParams])
  const handleBackToOverview = useCallback(() => {
    confirmPersonalDetailsNavigation(() => {
      if (selectedSection === 'security') { const next = new URLSearchParams(securityParams); next.delete('profileSection'); setSecurityParams(next, { replace: true }) }
      setSelectedSection(null)
      setPersonalDetailsDirty(false)
    }, 'Discard unsaved changes and return to your profile overview?')
  }, [confirmPersonalDetailsNavigation, selectedSection, securityParams, setSecurityParams])
  useMembersNativeBack(selectedSection === 'security' || selectedSection === 'achievements' && !achievementDetailsOpen, handleBackToOverview)
  const pollTimerRef = useRef(null)
  const saveRequestRef = useRef({})
  const confirmedSaveRequestRef = useRef({})
  const pendingSaveValueRef = useRef({})
  const lastSavedLevelsRef = useRef(readSportLevels(member))
  const hasHydratedLevelsRef = useRef(Object.values(SPORT_FIELDS).every(field => Object.hasOwn(member || {}, field)))
  const lastReadyAnimationUrlRef = useRef(null)
  const lastHydratedPortraitModeRef = useRef(null)
  const portraitModeSaveInFlightRef = useRef(false)

  const avatarUrl = useMemo(
    () => profile?.profile_picture || profile?.profile_picture_url || profile?.avatarUrl || null,
    [profile]
  )

  const memberName = useMemo(() => {
    if (!profile) return tx("membersStaff.member")
    return (
      profile.full_name ||
      [profile.first_name || profile.firstName, profile.last_name || profile.lastName]
        .filter(Boolean)
        .join(' ') ||
      profile.email ||
      tx("membersStaff.member")
    )
  }, [profile])

  const membershipNumber = useMemo(
    () => profile?.membership_number || profile?.member_number || profile?.member_id || profile?.id,
    [profile]
  )
  const membershipPresentation = useMemo(() => deriveMembershipPresentation({ status: overview.data?.membership?.status, membership_type: overview.data?.membership?.type }), [overview.data])


  const handleLanguageChange = async (value) => {
    setLanguageSaving(true)
    try {
      const response = await memberPortalApiPatch(`/api/members-portal/${encodeURIComponent(clubCode)}/me/language`, { preferred_locale: value === 'inherit' ? null : value })
      const body = await response.json()
      if (!response.ok || !body?.ok) throw new Error(body?.error || 'Unable to save language')
      const next = { ...(profile || {}), ...body.data }
      setProfile(next)
      onMemberProfileChange?.(next)
      const effectiveLocale = body.data.effective_locale
      setLocale(effectiveLocale)
      toast({ title: translate('language.saved', effectiveLocale) })
    } catch (error) { toast({ title: t('language.saveError'), variant: 'destructive' }) }
    finally { setLanguageSaving(false) }
  }

  const summaryState = overview.status === 'loading' ? t('profileOverview.loading') : overview.status === 'failed' ? t('profileOverview.error') : null
  const memberEmail = summaryState || overview.data?.contact_email || t('profileOverview.missing')
  const memberDetailNumber = summaryState || overview.data?.phone_number || t('profileOverview.missing')
  const memberDateOfBirth = summaryState || formatProfileCalendarDate(overview.data?.date_of_birth, locale) || t('profileOverview.missing')

  const hasReadyLivePortrait = animationState?.asset_type === 'video'
    && Boolean(animationState?.animation_asset_url)

  const displayedPortraitUrl = avatarUrl
  useEffect(() => { if (profile?.id) emitMemberClientTelemetry({ event: 'members_profile_record_available', endpointKey: 'profile_mobile_portrait', status: 200, totalMs: 0, cacheState: 'miss' }) }, [profile?.id])
  useEffect(() => { if (avatarUrl) emitMemberClientTelemetry({ event: 'members_avatar_url_available', endpointKey: 'profile_mobile_portrait', status: 200, totalMs: 0, cacheState: 'miss' }) }, [avatarUrl, profile?.id])
  const savedPortraitMode = normalizePortraitMode(profile?.profile_portrait_mode)

  const shouldRenderVideo = hasReadyLivePortrait
    && portraitMode === 'live'
    && videoLoadFailed !== true

  const loadAnimationState = useCallback(async () => {
    if (!clubCode || !profile?.id) return null
    try {
      const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/me/ai-profile-animation`)
      const json = await res.json()
      if (res.ok && json?.ok) {
        setAnimationState(json.data || null)
        if (json.data?.asset_type === 'video' && json.data?.animation_asset_url) {
          setVideoLoadFailed(false)
        }
        return json.data || null
      }
      return null
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[member-portal] unable to fetch animation state', err)
      }
      return null
    }
  }, [clubCode, memberPortalApiGet, profile?.id])

  useEffect(() => {
    if (member) {
      setProfile(member)
    } else {
      setProfile(null)
    }
  }, [member])


  useEffect(() => {
    const animationUrl = animationState?.animation_asset_url || null
    const lastHydrated = lastHydratedPortraitModeRef.current
    const savedPreferenceChanged = lastHydrated?.savedPortraitMode !== savedPortraitMode
    const readyStateChanged = lastHydrated?.hasReadyLivePortrait !== hasReadyLivePortrait
    const animationChanged = lastReadyAnimationUrlRef.current !== animationUrl && hasReadyLivePortrait

    lastReadyAnimationUrlRef.current = animationUrl

    if (videoLoadFailed === true) {
      setPortraitMode((currentMode) => (currentMode === 'photo' ? currentMode : 'photo'))
      lastHydratedPortraitModeRef.current = {
        savedPortraitMode,
        hasReadyLivePortrait,
        animationUrl,
        videoLoadFailed,
      }
      return
    }

    if (!hasReadyLivePortrait) {
      setPortraitMode((currentMode) => (currentMode === 'photo' ? currentMode : 'photo'))
      lastHydratedPortraitModeRef.current = {
        savedPortraitMode,
        hasReadyLivePortrait,
        animationUrl,
        videoLoadFailed,
      }
      return
    }

    if (portraitModeSaveInFlightRef.current) {
      lastHydratedPortraitModeRef.current = {
        savedPortraitMode,
        hasReadyLivePortrait,
        animationUrl,
        videoLoadFailed,
      }
      return
    }

    if (savedPreferenceChanged || readyStateChanged || animationChanged || !lastHydrated) {
      const nextMode = savedPortraitMode === 'live' ? 'live' : 'photo'
      setPortraitMode((currentMode) => (currentMode === nextMode ? currentMode : nextMode))
    }

    lastHydratedPortraitModeRef.current = {
      savedPortraitMode,
      hasReadyLivePortrait,
      animationUrl,
      videoLoadFailed,
    }
  }, [animationState?.animation_asset_url, hasReadyLivePortrait, savedPortraitMode, videoLoadFailed])

  useEffect(() => {
    if (!clubCode || !profile?.id) return undefined
    let cancelled = false

    async function hydrate() {
      const state = await loadAnimationState()
      if (cancelled) return
      if (!(state?.is_processing)) return

      const intervalMs = Number(state?.poll_after_ms) || 3500
      pollTimerRef.current = setInterval(async () => {
        const nextState = await loadAnimationState()
        if (!nextState?.is_processing && pollTimerRef.current) {
          clearInterval(pollTimerRef.current)
          pollTimerRef.current = null
        }
      }, intervalMs)
    }

    hydrate()
    return () => {
      cancelled = true
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [clubCode, loadAnimationState, profile?.id, profile?.profile_picture])

  useEffect(() => {
    if (!clubCode) return
    let cancelled = false

    async function hydrateProfile() {
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/me`)
        const json = await res.json()
        if (!cancelled && json?.ok) {
          setProfile(json.data)
          onMemberProfileChange?.(json.data)
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[member-portal] unable to fetch profile', err)
        }
      }
    }

    if (!member?.id) {
      hydrateProfile()
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, member?.id, memberPortalApiGet, onMemberProfileChange])

  useEffect(() => {
    if (selectedSection !== 'sports-playing-levels') return
    let cancelled = false
    const revision = { ...sportRevisions.current }
    setSportReadStatus('loading')
    async function read() {
      // A reopen must read after pending writes have reached the database.
      await waitForSportWrites(sportScope)
      if (cancelled) return
      try {
        const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/me/sport-levels?revision=${nextProfileReadRevision()}`)
        const body = await readMemberPortalJson(response)
        if (cancelled) return
        if (response?.ok === false || !body?.ok || !body.data || body.data.id !== member?.id) throw new Error('sports_read_failed')
        const next = readSportLevels(body.data)
        for (const key of Object.keys(SPORT_FIELDS)) {
          // Late initial reads cannot replace a newer interaction or confirmed write.
          if ((sportRevisions.current[key] || 0) !== (revision[key] || 0)) continue
          lastSavedLevelsRef.current[key] = next[key]
          if (!sportEdits.current[key]) setLevels(current => ({ ...current, [key]: next[key] }))
          setConfirmedLevels(current => ({ ...current, [key]: next[key] }))
        }
        hasHydratedLevelsRef.current = true
        setSportReadStatus('resolved')
      } catch { if (!cancelled) setSportReadStatus('failed') }
    }
    read()
    return () => { cancelled = true }
  }, [sportScope, clubCode, member?.id, memberPortalApiGet, sportReadRetry, selectedSection])

  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current)
  }, [])

  const persistSportLevel = useCallback(async (key, value) => {
    if (!clubCode || (lastSavedLevelsRef.current[key] === value && pendingSaveValueRef.current[key] === undefined) || pendingSaveValueRef.current[key] === value) return
    pendingSaveValueRef.current[key] = value
    sportRevisions.current[key] = (sportRevisions.current[key] || 0) + 1
    const requestId = (saveRequestRef.current[key] || 0) + 1
    saveRequestRef.current[key] = requestId
    setSaveStates((current) => ({ ...current, [key]: 'saving' }))
    const field = key === 'golf' ? 'golf_handicap' : `${key}_rating`
    try {
      const { res, json } = await enqueueSportWrite(sportScope, async () => {
        const res = await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/sport-levels`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }),
        })
        return { res, json: await res.json() }
      })
      if (!sportsMounted.current) return
      if (saveRequestRef.current[key] === requestId) pendingSaveValueRef.current[key] = undefined
      if (!res.ok || !json?.ok) throw new Error(json?.error || 'Unable to save this level.')
      const serverValue = json.data?.[field]
      if (!Object.hasOwn(json.data || {}, field) || (serverValue !== null && !Number.isFinite(Number(serverValue)))) throw new Error('invalid_saved_response')
      const normalized = serverValue === null ? null : key === 'golf' ? normalizeGolfValue(serverValue, null) : normalizeRatingValue(serverValue, null)
      if (requestId < (confirmedSaveRequestRef.current[key] || 0)) return
      confirmedSaveRequestRef.current[key] = requestId
      lastSavedLevelsRef.current = { ...lastSavedLevelsRef.current, [key]: normalized }
      setConfirmedLevels((current) => ({ ...current, [key]: normalized }))
      if (saveRequestRef.current[key] === requestId) {
        sportEdits.current[key] = false
        setLevels((current) => ({ ...current, [key]: normalized }))
        setSaveStates((current) => ({ ...current, [key]: 'saved' }))
      }
      setProfile((current) => ({ ...(current || {}), [field]: normalized }))
      onMemberProfileChange?.({ id: member?.id, [field]: normalized })
    } catch {
      if (!sportsMounted.current || saveRequestRef.current[key] !== requestId) return
      pendingSaveValueRef.current[key] = undefined
      setSaveStates((current) => ({ ...current, [key]: 'error' }))
    }
  }, [clubCode, sportScope, member?.id, memberPortalApiFetch, onMemberProfileChange])

  const commitSportLevel = useCallback((key, rawValue) => {
    if (!hasHydratedLevelsRef.current || saveStates[key] !== 'dirty') return
    const fallback = levels[key] ?? DEFAULT_LEVELS[key]
    const value = key === 'golf' ? normalizeGolfValue(rawValue, fallback) : normalizeRatingValue(rawValue, fallback)
    persistSportLevel(key, value)
  }, [levels, persistSportLevel, saveStates])

  const handleSliderChange = (key, value) => {
    sportEdits.current[key] = true
    sportRevisions.current[key] = (sportRevisions.current[key] || 0) + 1
    const fallback = levels[key] ?? DEFAULT_LEVELS[key]
    const normalized = key === 'golf' ? normalizeGolfValue(value, fallback) : normalizeRatingValue(value, fallback)
    setLevels((current) => ({ ...current, [key]: normalized }))
    setSaveStates((current) => ({ ...current, [key]: 'dirty' }))
  }


  const submitGolfEntry = () => {
    if (!hasHydratedLevelsRef.current) return
    const entered = parseGolfHandicapEntry(golfEntry)
    if (entered == null) {
      setGolfEntryError(memberMessage("memberWorkflows.profile.enter00360OrUseForA"))
      return
    }
    setGolfEntryError('')
    sportEdits.current.golf = true
    setLevels((current) => ({ ...current, golf: entered }))
    setSaveStates((current) => ({ ...current, golf: 'dirty' }))
    persistSportLevel('golf', entered)
  }

  const handleGolfEntryChange = (event) => {
    const nextEntry = event.target.value
    setGolfEntry(nextEntry)
    if (golfEntryError && parseGolfHandicapEntry(nextEntry) != null) {
      setGolfEntryError('')
    }
  }

  const persistPortraitMode = useCallback(
    async (nextMode) => {
      if (!clubCode) return false

      try {
        const res = await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/portrait-mode`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile_portrait_mode: nextMode }),
        })
        const json = await res.json()

        if (res.ok && json?.ok) {
          portraitModeSaveInFlightRef.current = false
          setPortraitModeSaveError('')
          setProfile((prev) => {
            const merged = { ...(prev || {}), ...(json.data || {}), profile_portrait_mode: json.data?.profile_portrait_mode || nextMode }
            onMemberProfileChange?.(merged)
            return merged
          })
          return true
        }

        portraitModeSaveInFlightRef.current = false
        setPortraitModeSaveError(memberMessage("memberWorkflows.profile.unableToSavePortraitPreferenceRightNow"))
        return false
      } catch (err) {
        portraitModeSaveInFlightRef.current = false
        setPortraitModeSaveError(memberMessage("memberWorkflows.profile.unableToSavePortraitPreferenceRightNow"))
        return false
      }
    },
    [clubCode, memberPortalApiFetch, onMemberProfileChange]
  )

  const handlePortraitToggle = useCallback(async () => {
    const nextMode = portraitMode === 'live' ? 'photo' : 'live'
    portraitModeSaveInFlightRef.current = true
    setVideoLoadFailed(false)
    setPortraitMode(nextMode)
    setPortraitModeSaveError('')

    const didPersist = await persistPortraitMode(nextMode)
    if (!didPersist) {
      const fallbackMode = savedPortraitMode === 'photo' ? 'photo' : (hasReadyLivePortrait ? 'live' : 'photo')
      setPortraitMode(fallbackMode)
    }
  }, [hasReadyLivePortrait, persistPortraitMode, portraitMode, savedPortraitMode])

  const handleUpload = async (file) => {
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      setPhotoError(memberMessage("memberWorkflows.profile.pleaseSelectAnImageFile"))
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setPhotoError(memberMessage("memberWorkflows.profile.pleaseChooseAnImageUnder8MB"))
      return
    }

    setIsUploading(true)
    setPhotoError('')
    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/me/avatar`, formData)
      const json = await res.json()
      if (res.ok && json?.ok && json.url) {
        const updated = { ...(profile || {}), profile_picture: json.url, profile_picture_url: json.url, avatarUrl: json.url }
        setProfile(updated)
        setAnimationState(null)
        setVideoLoadFailed(false)
        onMemberProfileChange?.(updated)
        toast({ title: tx("racquetPlay.saved"), description: tx("memberWorkflows.profile.profilePhotoUpdated") })
        setIsModalOpen(false)
      } else {
        setPhotoError(memberMessage("membersStaff.uploadFailed"))
      }
    } catch (err) {
      setPhotoError(memberMessage("membersStaff.uploadFailed"))
    } finally {
      setIsUploading(false)
    }
  }

  const handleBringProfileToLife = async () => {
    if (!clubCode || animationLoading) return
    setAnimationLoading(true)
    try {
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/me/ai-profile-animation`, {})
      const json = await res.json()
      if (res.ok && json?.ok) {
        const nextState = json.data || null
        setAnimationState((prev) => ({ ...(prev || {}), ...(nextState || {}) }))
        if (nextState?.asset_type === 'video' && nextState?.animation_asset_url) setVideoLoadFailed(false)
        if (nextState?.is_processing) {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current)
          const intervalMs = Number(nextState?.poll_after_ms) || 3500
          pollTimerRef.current = setInterval(async () => {
            const refreshed = await loadAnimationState()
            if (!refreshed?.is_processing && pollTimerRef.current) {
              clearInterval(pollTimerRef.current)
              pollTimerRef.current = null
              if (refreshed?.has_ready_asset) {
                toast({ title: tx("memberWorkflows.profile.ready"), description: tx("memberWorkflows.profile.yourPortraitIsNowLive") })
              }
            }
          }, intervalMs)
        }
        if (nextState?.is_processing) {
          toast({ title: tx("racquetPlay.in_progress"), description: tx("memberWorkflows.profile.weArePreparingYourPortrait") })
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[member-portal] unable to request animated portrait', err)
      }
    } finally {
      setAnimationLoading(false)
    }
  }

  const handleRemovePhoto = async () => {
    if (!clubCode) return
    setIsUploading(true)
    setPhotoError('')
    try {
      const res = await memberPortalApiFetch(`/api/members-portal/${clubCode}/me/avatar`, { method: 'DELETE' })
      const json = await res.json()
      if (res.ok && json?.ok) {
        const updated = { ...(profile || {}), profile_picture: null, profile_picture_url: null, avatarUrl: null }
        setProfile(updated)
        setAnimationState(null)
        setVideoLoadFailed(false)
        onMemberProfileChange?.(updated)
        toast({ title: tx("racquetPlay.saved"), description: tx("memberWorkflows.profile.profilePhotoRemoved") })
        setIsModalOpen(false)
      } else {
        setPhotoError(memberMessage("memberWorkflows.profile.unableToRemovePhoto"))
      }
    } catch (err) {
      setPhotoError(memberMessage("memberWorkflows.profile.unableToRemovePhoto"))
    } finally {
      setIsUploading(false)
    }
  }

  const savedSports = sportLevels.filter((sport) => Number.isFinite(confirmedLevels[sport.key]))
  const sportsPlayingLevelsSection = (
    <section className="mp-sporting-profile" aria-labelledby="sporting-profile-intro">
      <header className="mp-sporting-intro">
        <Trophy className="mp-sporting-intro__icon" size={24} aria-hidden="true" />
        <h4 id="sporting-profile-intro">{t('memberSports.title')}</h4>
        <p className="mp-sports-compact-copy">{portalText("memberRemaining.m14")}</p>
        <p className="mp-sports-desktop-copy">{t('memberSports.intro')}</p>
      </header>
      {sportReadStatus === 'failed' ? <p role="alert">{renderMemberMessage(tx, profileMessageDescriptor('load'))} <button type="button" onClick={()=>setSportReadRetry(value=>value+1)}>{portalText("memberAccount.retry")}</button></p> : null}
      {sportReadStatus === 'loading' && !hasHydratedLevelsRef.current ? <p role="status">{locale === 'es' ? 'Cargando niveles…' : 'Loading levels…'}</p> : null}
      <div className="mp-sport-cards">
        {sportLevels.map((sport) => {
          const value = levels[sport.key]
          const isSet = Number.isFinite(value)
          const isGolf = sport.key === 'golf'
          const expanded = expandedSport === sport.key
          const primary = !isSet ? tx("memberWorkflows.profile.notSet") : isGolf ? formatGolfHandicap(value) : tx(PROFILE_ENUM_KEYS[levelLabel(value)])
          const secondary = !isSet ? tx("memberWorkflows.profile.addYourLevelForBetterMatchesAndRecommendations") : isGolf ? tx("memberWorkflows.profile.handicap") : tx("memberWorkflows.profile.levelNumber", {number:Number(value).toLocaleString(locale,{minimumFractionDigits:1,maximumFractionDigits:1})})
          const status = saveStates[sport.key]
          const statusText = status === 'saving' ? tx("golfStats.saving") : status === 'saved' ? tx("racquetPlay.saved") : status === 'error' ? tx("memberWorkflows.profile.notSaved") : ''
          return <article className={`mp-sport-card${expanded ? ' is-expanded' : ''}`} key={sport.key} data-sport={sport.key}>
            <button type="button" className="mp-sport-card__header" aria-expanded={expanded} aria-controls={`sport-editor-${sport.key}`} onClick={() => setExpandedSport(expanded ? null : sport.key)}>
              <span className={`mp-sport-icon mp-sport-icon--${sport.key}`} aria-hidden="true"><CanonicalSportIcon sport={sport} /></span>
              <span className="mp-sport-card__identity"><span className="mp-sport-card__name">{(sport.key === 'golf' ? t('achievements.golf') : t(`racquetPlay.${sport.key}`))}</span><strong>{primary}</strong><small className={!isSet ? 'mp-sport-card__generic-help' : undefined}>{secondary}</small></span>
              <span className="mp-sport-card__edit"><span className="mp-sports-compact-copy">{isSet ? tx("racquetPlay.edit") : tx("memberWorkflows.profile.setLevel")}</span><span className="mp-sports-desktop-copy">{t('memberSports.setLevel')}</span><ChevronDown size={16} aria-hidden="true"/></span>
            </button>
            {expanded ? <div className="mp-sport-editor" id={`sport-editor-${sport.key}`}>
              <div className="mp-sport-editor__current"><span>{isGolf ? tx("memberWorkflows.profile.currentHandicap") : tx("memberWorkflows.profile.currentTier")}</span><strong>{primary}</strong></div>
              <input type="range" disabled={!hasHydratedLevelsRef.current} min={sport.min} max={sport.max} step={isGolf ? 0.1 : sport.step} value={isSet ? value : DEFAULT_LEVELS[sport.key]}
                onChange={(event) => handleSliderChange(sport.key, event.target.value)} onPointerUp={(event) => commitSportLevel(sport.key, event.currentTarget.value)} onPointerCancel={(event) => commitSportLevel(sport.key, event.currentTarget.value)} onTouchEnd={(event) => commitSportLevel(sport.key, event.currentTarget.value)} onKeyUp={(event) => commitSportLevel(sport.key, event.currentTarget.value)} onBlur={(event) => commitSportLevel(sport.key, event.currentTarget.value)}
                aria-label={isGolf ? tx("memberWorkflows.profile.golfHandicap") : tx("memberWorkflows.profile.sportPlayingLevel", {sport:t(`racquetPlay.${sport.key}`)})} aria-valuetext={isGolf ? tx("memberWorkflows.profile.golfHandicapNumber", {number:formatGolfHandicap(isSet ? value : DEFAULT_LEVELS.golf)}) : tx("memberWorkflows.profile.sportTierLevelNumber", {sport:t(`racquetPlay.${sport.key}`),tier:tx(PROFILE_ENUM_KEYS[levelLabel(isSet ? value : DEFAULT_LEVELS[sport.key])]),number:Number(isSet ? value : DEFAULT_LEVELS[sport.key]).toLocaleString(locale,{minimumFractionDigits:1,maximumFractionDigits:1})})} />
              <div className="mp-sport-scale"><span>{isGolf ? '36.0' : tx("memberWorkflows.profile.recreational")}</span><span>{isGolf ? '+5.0' : tx("memberWorkflows.profile.elite")}</span></div>
              {isGolf ? <div className="mp-golf-precise"><label htmlFor="golf-handicap-entry">{portalText("memberRemaining.m15")}</label><div><input id="golf-handicap-entry" disabled={!hasHydratedLevelsRef.current} inputMode="decimal" type="text" pattern="\+?[0-9]+(?:\.[0-9])?" value={golfEntry} placeholder={isSet ? formatGolfHandicap(value) : tx('memberWorkflows.profile.notSet')} onChange={handleGolfEntryChange} aria-describedby={golfEntryError ? 'golf-entry-error' : undefined}/><button type="button" disabled={!hasHydratedLevelsRef.current} onClick={submitGolfEntry}>{portalText("epos.extra321")}</button></div>{golfEntryError ? <p id="golf-entry-error" role="alert">{renderMemberMessage(tx, golfEntryError)}</p> : null}</div> : null}
              <div className={`mp-sport-save mp-sport-save--${status || 'idle'}`} aria-live="polite">{statusText}{status === 'error' ? <button type="button" onClick={() => persistSportLevel(sport.key, value)}><RotateCcw size={14}/> {portalText("memberAccount.retry")}</button> : null}</div>
            </div> : null}
          </article>
        })}
      </div>
      <section className="mp-sport-summary" aria-labelledby="sport-summary-title"><h4 id="sport-summary-title">{portalText("memberSports.title")}</h4><p>{portalText("memberRemaining.m16")}</p>{savedSports.length ? <ul>{savedSports.map((sport) => { const saved = confirmedLevels[sport.key]; return <li key={sport.key}><span>{(sport.key === 'golf' ? t('achievements.golf') : t(`racquetPlay.${sport.key}`))}</span><strong>{sport.key === 'golf' ? tx("memberWorkflows.profile.handicapNumber", {number:formatGolfHandicap(saved)}) : tx("memberWorkflows.profile.tierLevel", {tier:tx(PROFILE_ENUM_KEYS[levelLabel(saved)])})}</strong></li> })}</ul> : <p className="mp-sport-summary__empty">{portalText("memberRemaining.m17")}</p>}</section>
    </section>
  )

  return (
    <div className={`mp-profile-page mp-profile-page--${profilePageMode}`}>
      <div className="mp-profile-layout">
        <section className="mp-profile-hero mp-profile-hero--immersive">
          <div className="mp-profile-media-stage">
            <div className="mp-profile-portrait-wrap">
              <div className="mp-profile-media-frame">
                {shouldRenderVideo ? (
                  <video
                    key={animationState?.animation_asset_url}
                    src={animationState?.animation_asset_url}
                    poster={animationState?.animation_preview_image_url || avatarUrl || undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onError={() => {
                      setVideoLoadFailed(true)
                      setPortraitMode('photo')
                    }}
                    aria-label={tx("memberWorkflows.profile.nameLivePortrait", {name:memberName})}
                  />
                ) : displayedPortraitUrl ? (
                  <TimedMemberImage key={`${profile?.id || 'member'}:${displayedPortraitUrl}`} identityKey={profile?.id || ''} src={displayedPortraitUrl} category="profile_mobile_portrait" sourceType="signed_url" alt={memberName} fallback={<div className="mp-profile-media-fallback">{memberName?.[0] || 'M'}</div>} />
                ) : (
                  <div className="mp-profile-media-fallback">{memberName?.[0] || 'M'}</div>
                )}
              </div>
              <button
                type="button"
                className="mp-profile-mobile-edit-photo"
                onClick={() => setIsModalOpen(true)}
                aria-label={portalText("memberRemaining.m18")}
              >
                <Pencil size={19} aria-hidden="true" />
              </button>
            </div>
            <div className="mp-profile-media-overlay">
              <div className="mp-profile-media-chips">
                {membershipPresentation.statusLabel ? <span className={`mp-pill ${membershipPresentation.statusVariant}`}>{localizeMembershipStatus(overview.data?.membership?.status, t)}</span> : null}
                {membershipPresentation.accessLabel ? <span className="mp-pill subtle">{membershipPresentation.accessLabel}</span> : null}
                {animationState?.has_ready_asset ? <span className="mp-pill muted">{portalText("memberRemaining.m19")}</span> : null}
              </div>
              <div className="mp-profile-media-actions">
                {animationState?.show_cta ? (
                  <button type="button" className="mp-chip mp-chip--primary mp-profile-media-action--secondary" onClick={handleBringProfileToLife} disabled={animationLoading}>
                    <Sparkles size={16} /> {animationLoading ? tx("memberWorkflows.profile.preparing") : tx("memberWorkflows.profile.bringProfileToLife")}
                  </button>
                ) : null}
                {hasReadyLivePortrait ? (
                  <button
                    type="button"
                    className="mp-chip mp-profile-media-action--secondary"
                    onClick={handlePortraitToggle}
                  >
                    {portraitMode === 'live' ? tx("memberWorkflows.profile.viewPhoto") : tx("memberWorkflows.profile.viewLivePortrait")}
                  </button>
                ) : null}
                {portraitModeSaveError ? <p className="mp-error mp-profile-media-action-error" style={{ margin: '8px 0 0' }}>{renderMemberMessage(tx, portraitModeSaveError)}</p> : null}
                <button type="button" className="mp-chip mp-profile-media-action--edit" onClick={() => setIsModalOpen(true)}>
                  <Camera size={16} /> {portalText("memberRemaining.m18")}
                </button>
              </div>
            </div>
          </div>
          <div className="mp-profile-hero__body">
            <div className="mp-profile-identity-tile">
              <span>{portalText("memberRemaining.m20")}{membershipNumber || '—'}</span>
              <strong>{memberName}</strong>
            </div>
            <div className="mp-profile-mobile-status" aria-label={portalText("membershipInformation.status")}>
              {membershipPresentation.statusLabel ? <span className={`mp-pill ${membershipPresentation.statusVariant}`}>{localizeMembershipStatus(overview.data?.membership?.status, t)}</span> : null}
              {membershipPresentation.accessLabel ? <span className="mp-pill subtle">{membershipPresentation.accessLabel}</span> : null}
              {animationState?.has_ready_asset ? <span className="mp-pill muted">{portalText("memberRemaining.m19")}</span> : null}
            </div>
            {animationState?.is_processing ? (
              <p className="mp-profile-hero__note">{portalText("memberRemaining.m21")}</p>
            ) : null}
            <dl className="mp-profile-detail-list" aria-busy={overview.status === 'loading'}>
              <div>
                <span className="mp-profile-detail-list__icon" aria-hidden="true"><Mail size={17} /></span>
                <dt>{t('profileOverview.email')}</dt>
                <dd title={memberEmail || undefined} aria-label={memberEmail}>{memberEmail || '—'}</dd>
              </div>
              <div>
                <span className="mp-profile-detail-list__icon" aria-hidden="true"><Phone size={17} /></span>
                <dt>{t('profileOverview.phone')}</dt>
                <dd title={memberDetailNumber || undefined}>{memberDetailNumber || '—'}</dd>
              </div>
              <div>
                <span className="mp-profile-detail-list__icon" aria-hidden="true"><CalendarClock size={17} /></span>
                <dt>{t('profileOverview.dob')}</dt>
                <dd title={memberDateOfBirth || undefined}>{memberDateOfBirth || '—'}</dd>
              </div>
            </dl>
          </div>
        </section>

        <div className="mp-profile-content">
          {selectedSection ? (
            <div className="mp-profile-section-page">
              <div className={selectedSection === 'personal-details' ? 'mp-personal-details-scroll' : 'mp-profile-section-surface'}>
                <ProfileSectionPageHeader section={selectedSection} onBack={handleBackToOverview} />
                <ProfileSectionRenderer memberId={member?.id} isMobileApp={isMobileApp} personalResource={personalResource} onRefreshSummary={()=>overview.refresh({ afterSave: true })} achievementsSection={<MemberAchievements isMobileApp={isMobileApp} onDetailsOpenChange={setAchievementDetailsOpen} clubCode={clubCode} scope={`${identityScope}:${clubCode}:${member?.id || ''}`} get={memberPortalApiGet} />} digitalCardSection={<MemberDigitalMembershipCard overview={overview} branding={branding} member={profile} />} securitySection={<MemberSecurity key={`${identityScope}:${clubCode}:${member?.id || ''}`} scope={`${identityScope}:${clubCode}:${member?.id || ''}`} clubCode={clubCode} get={memberPortalApiGet} request={memberPortalApiFetch} onLogout={onLogout} isLoggingOut={isLoggingOut} />} membershipSection={<MemberMembershipInformation onContactFrontDesk={() => setFrontDeskOpen(true)} overview={overview} clubCode={clubCode} memberId={member?.id} scope={`${identityScope}:${clubCode}:${member?.id || ''}`} get={memberPortalApiGet} isMobileApp={isMobileApp} />} section={selectedSection} sportsSection={sportsPlayingLevelsSection} clubCode={clubCode} memberPortalApiGet={memberPortalApiGet} memberPortalApiFetch={memberPortalApiFetch} onProfileChange={handlePersonalProfileChange} onDirtyChange={setPersonalDetailsDirty} />
              </div>
            </div>
          ) : (
            <>
              <ProfileSectionNavigation onSelectSection={handleSelectSection} />
              {!isMobileApp ? <ProfileHubPanel overview={overview} membershipNumber={overview.data?.membership_number} onViewMembership={() => handleSelectSection('membership-information')} /> : null}
              <section className="mp-language-card"><div className="mp-language-card__heading"><span className="mp-language-card__icon" aria-hidden="true"><Languages size={21} /></span><div><h2>{t('language.label')}</h2><p>{t('language.profileHelp')}</p></div></div><LanguageSelector portal appearance="members" value={profile?.preferred_locale || 'inherit'} disabled={languageSaving} onValueChange={handleLanguageChange} inheritLabel={`${t('language.useClubDefault')} — ${PRIVANA_LOCALES[profile?.club_default_locale]?.nativeName || PRIVANA_LOCALES['en-GB'].nativeName}`} /></section>
              <MobileProfileLogoutSection onLogout={onLogout} isLoggingOut={isLoggingOut} />
            </>
          )}
        </div>
      </div>

      {frontDeskOpen ? <MemberFrontDeskModal clubCode={clubCode} onClose={() => setFrontDeskOpen(false)} /> : null}
      {isModalOpen && (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setIsModalOpen(false)} />
          <div className="mp-modal__panel">
            <div className="mp-modal__header">
              <h4>{portalText("memberRemaining.m18")}</h4>
              <button type="button" className="icon-button" aria-label={t('common.actions.close')} onClick={() => setIsModalOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="mp-modal__content">
              <div className="mp-modal__preview">
                {avatarUrl ? <img src={avatarUrl} alt={tx("memberWorkflows.profile.currentAvatar")} /> : <span>{portalText("rooms.roomForm.noPhoto")}</span>}
              </div>
              <div className="mp-modal__actions">
                <label className="mp-chip" htmlFor="member-avatar-input">
                  <Camera size={16} />
                  {isUploading ? tx("memberWorkflows.profile.uploading") : tx("memberWorkflows.profile.uploadNewPhoto")}
                </label>
                <input type="file" id="member-avatar-input" accept="image/*" style={{ display: 'none' }} onChange={(event) => handleUpload(event.target.files?.[0])} disabled={isUploading} />
                <button type="button" className="mp-chip danger" onClick={handleRemovePhoto} disabled={isUploading}>
                  <Trash2 size={16} /> {portalText("membersStaff.removePhoto")}
                </button>
              </div>
              {photoError ? <p className="mp-error">{renderMemberMessage(tx, photoError)}</p> : null}
              <p className="mp-muted">{portalText("memberRemaining.m22")}</p>
            </div>
            <div className="mp-modal__footer">
              <button type="button" className="mp-ghost" onClick={() => setIsModalOpen(false)} disabled={isUploading}>{portalText("roomScreen.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function MemberProfilePage(props) {
  return <MemberPortalConfirmProvider><MemberProfilePageContent key={`${props.identityScope || ''}:${props.clubCode}:${props.member?.id || ''}`} {...props} /></MemberPortalConfirmProvider>
}
